import { formatInTimeZone } from "date-fns-tz";
import { useCallback } from "react";
import { IconButton, Typography, SidePanel, Icon, Card, Divider, Alert, Tooltip } from "@/components/atoms";
import { BookingFlight, BookingFlightDeparture, BookingFlightStatus } from "@/redux/slices/booking/types";
import { arrivalFlightOf, departureFlightOf } from "../../helpers/stopFlights";
import { clsx, getAssetUrl } from "@/utils";
import { BookingFlightStatusCode } from "../../common/BookingFlightStatusCode/BookingFlightStatusCode";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";

/**
 * Which end of the flight the panel describes (DEV-1487). `"arrival"` — the default, and every caller
 * before this ticket — is the pickup's. `"departure"` is the drop-off's.
 *
 * Parameterised rather than copied for the reason `FlightCard` was: two panels describing one flight
 * object drift on the labels, and the labels are the entire difference between "Landing Time" and
 * "Departure Time" being right or dangerously wrong.
 */
type FlightLeg = "arrival" | "departure";

interface FlightDetailsProps {
  open: boolean;
  onClose: () => void;
  leg?: FlightLeg;
}

interface DetailsProps {
  flight: BookingFlight;
  timezone: string;
  leg: FlightLeg;
}

export const FlightDetails = ({ open, onClose, leg = "arrival" }: FlightDetailsProps) => {
  const booking = useSelectedBooking();
  const isDeparture = leg === "departure";
  // Each leg off its own stop — never a search across every stop, which hands a drop-off booking's
  // departure flight to the arrival panel and describes the wrong end of its own route.
  const flight = isDeparture ? departureFlightOf(booking.stops) : arrivalFlightOf(booking.stops);

  return (
    <SidePanel open={open} onClose={onClose} width={550} focused>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="Airplane" className="text-neutral-dark-gray" size="lg" />
          {/* Exactly the card's own label, so the two agree word for word. No job number: this panel
              only ever opens from inside a booking whose header already names it. */}
          <Typography variant="title">{`Flight Details (${isDeparture ? "Departure" : "Arrival"})`}</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={onClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <section>{flight && <Details flight={flight} timezone={booking.dates.timezone} leg={leg} />}</section>
    </SidePanel>
  );
};

const Details = ({ flight, timezone, leg }: DetailsProps) => {
  const { status, number, codeShare, operatingAs, delay } = flight;
  const isDeparture = leg === "departure";
  // The leg this panel describes. `BookingFlightDeparture` is { terminal, scheduled, estimated,
  // actual, airport } — the arrival adds the three gate columns, which is the whole reason the gate
  // rows below are arrival-only.
  const stopLeg = isDeparture ? flight.departure : flight.arrival;
  const statusClasses = { "is-cancelled": status === "cancelled" };

  const renderAlerts = () => {
    switch (status) {
      case "not_found":
        return <Alert type="warning" message="Flight details cannot be verified" />;

      case "unknown":
        return <Alert type="info" message="Flight details will be available 24 hours before the flight." />;
      default:
        return null;
    }
  };

  const renderFlightStatusCode = () => {
    const activeStatusList = ["scheduled", "en-route"];
    if (typeof delay === "number" && activeStatusList.includes(status) && delay !== 0) {
      return (
        <Tooltip content={`Running ${Math.abs(delay)} minutes ${delay > 0 ? "late" : "early"}`} placement="left">
          <BookingFlightStatusCode status={status} delay={delay} type="default" />
        </Tooltip>
      );
    }

    return <BookingFlightStatusCode status={status} type="default" />;
  };

  return (
    <div className={clsx("group flex min-h-full flex-col space-y-2.5 p-5", statusClasses)}>
      {renderAlerts()}
      <Card className="flex flex-col">
        <div className="flex flex-row items-center">
          <div className="flex flex-row">
            {flight.airline?.avatar ? (
              <img
                src={getAssetUrl(flight.airline?.avatar, { height: 48, width: 250, fit: "cover" })}
                alt={flight.airline.name}
                className="h-[24px] w-[125px]"
              />
            ) : (
              <Typography variant="action">{flight.airline?.name || "-"}</Typography>
            )}
          </div>
          <div className="flex flex-1 justify-end">{renderFlightStatusCode()}</div>
        </div>
        <Typography variant="h2" className="mt-3">{`Flight ${number}`}</Typography>
        <Typography variant="paragraph" className="mt-1">
          {codeShare
            ? [operatingAs, ...codeShare]
                .filter((code) => code !== number)
                .filter(Boolean)
                .join(", ")
            : ""}
        </Typography>
      </Card>
      <Card className="flex flex-col gap-y-1">
        <Typography variant="action">{isDeparture ? "Departure" : "Arrival"}</Typography>
        <div className="flex flex-row items-center gap-x-1.5">
          <Icon name="Location" className="text-neutral-dark-gray" size="md" />
          <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
            {stopLeg.airport?.name || "-"}
          </Typography>
        </div>
      </Card>
      <Card className="flex flex-col gap-y-1">
        {/* "Gate Time" is an arrival word. On a departure this is the scheduled DEPARTURE, off the same
            `scheduled` column — the label is the only thing that changes, and it has to. */}
        <Typography variant="action">{isDeparture ? "Scheduled Departure Time" : "Scheduled Gate Time"}</Typography>
        <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
          {stopLeg.scheduled ? formatInTimeZone(new Date(stopLeg.scheduled), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
        </Typography>
      </Card>
      <Card className="flex flex-col gap-y-3">
        <LegTime leg={stopLeg} timezone={timezone} noun={isDeparture ? "Departure" : "Landing"} />
        {/* NO gate row on a departure. `FlightDeparture` carries no gate columns at all, so the row
            could only ever render "-" — and a permanently empty row reads as missing data rather than
            as data that does not exist. */}
        {!isDeparture && (
          <>
            <Divider />
            <GateTime flight={flight} timezone={timezone} />
          </>
        )}
        <Divider />
        <div className="flex flex-col gap-y-1">
          <Typography variant="action">Terminal</Typography>
          <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
            {stopLeg.terminal || "-"}
          </Typography>
        </div>
      </Card>
      <FlightProgress
        status={status}
        isDeparture={isDeparture}
        // On a departure the only rung that means anything to a drop-off booking is "has it left yet",
        // and `departure.actual` is the one column that answers it.
        hasDeparted={!!flight.departure.actual}
        hasArrived={!!flight.arrival.actual}
        hasGated={!!flight.arrival.gateActual}
      />
    </div>
  );
};

/**
 * How far along the flight is.
 *
 * The DEPARTURE variant is one rung, deliberately. The arrival's three — Departed / Landed /
 * Arrived-Gated — answer "is my passenger off the plane yet", which is the pickup's question. A
 * drop-off booking asks only "has it left", and the two rungs below that cannot be answered from a
 * departure leg anyway: `landed` describes the far end of a route this booking has nothing to do
 * with, and there is no gate column at all. Adding rungs that describe someone else's journey would
 * be worse than a short card.
 */
const FlightProgress = ({
  status,
  isDeparture,
  hasDeparted,
  hasArrived,
  hasGated,
}: {
  status: BookingFlightStatus;
  isDeparture: boolean;
  hasDeparted: boolean;
  hasArrived: boolean;
  hasGated: boolean;
}) => {
  const renderDepartureStatusIcon = useCallback(() => {
    switch (status) {
      case "diverted":
      case "en-route":
      case "early":
      case "landed":
        return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;

      case "cancelled":
        return <Icon name="CloseCircle" size="md" variant="Bold" className="text-danger" />;

      default:
        return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
    }
  }, [status]);

  const renderLandedStatusIcon = useCallback(() => {
    switch (status) {
      case "landed":
        return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;

      case "cancelled":
        return <Icon name="CloseCircle" size="md" variant="Bold" className="text-danger" />;

      default:
        return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
    }
  }, [status]);

  const renderGatedStatus = useCallback(() => {
    if (hasGated) {
      return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;
    }

    return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
  }, [hasGated]);

  // A departure that has actually gone is a fact off its own column; otherwise fall back to the
  // status, which is what the arrival rung reads.
  const renderDepartedStatus = useCallback(() => {
    if (hasDeparted) return <Icon name="TickCircle" size="md" variant="Bold" className="text-success" />;
    if (status === "cancelled") return <Icon name="CloseCircle" size="md" variant="Bold" className="text-danger" />;

    return <Icon name="MinusCircle" size="md" variant="Bold" className="text-neutral-dark-gray" />;
  }, [hasDeparted, status]);

  if (isDeparture) {
    return (
      <Card>
        <Typography variant="action">Flight Progress</Typography>
        <div className="mt-3 grid gap-2">
          <Typography className="flex items-center gap-x-2" variant="paragraph">
            {renderDepartedStatus()}
            Departed
          </Typography>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Typography variant="action">Flight Progress</Typography>
      <div className="mt-3 grid gap-2">
        <Typography className="flex items-center gap-x-2" variant="paragraph">
          {renderDepartureStatusIcon()}
          Departed
        </Typography>
        <Typography className="flex items-center gap-x-2" variant="paragraph">
          {renderLandedStatusIcon()}
          Landed
        </Typography>
        {status === "landed" && hasArrived && (
          <Typography className="flex items-center gap-x-2" variant="paragraph">
            {renderGatedStatus()}
            Arrived / Gated
          </Typography>
        )}
      </div>
    </Card>
  );
};

/**
 * The leg's best-known time — actual if it has happened, else the estimate. One component for both
 * ends, taking the NOUN, because the arithmetic is identical and only the word differs: "Landing" on
 * an arrival, "Departure" on a departure. The word "Landing" must never appear on a departure panel.
 */
const LegTime = ({ leg, timezone, noun }: { leg: BookingFlightDeparture; timezone: string; noun: string }) => {
  const isActual = leg.actual;
  const time = isActual ? leg.actual : leg.estimated;

  return (
    <div className="flex flex-col gap-y-1">
      <Typography variant="action">{`${isActual ? "Actual" : "Est."} ${noun} Time`}</Typography>
      <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
        {time ? formatInTimeZone(new Date(time), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
      </Typography>
    </div>
  );
};

const GateTime = ({ flight, timezone }: { flight: BookingFlight; timezone: string }) => {
  const { arrival } = flight;
  const isActual = arrival.gateActual;
  const gateTime = isActual ? arrival.gateActual : arrival.gateEstimated;

  return (
    <div className="flex flex-col gap-y-1">
      <Typography variant="action">{isActual ? "Actual" : "Est."} Gate Time</Typography>
      <Typography variant="paragraph" className="group-[.is-cancelled]:line-through">
        {gateTime ? formatInTimeZone(new Date(gateTime), timezone, "d MMM yyyy (EEE), h:mm a") : "-"}
      </Typography>
    </div>
  );
};
